import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Folder from '@mui/icons-material/Folder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Typography} from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import {CreditAccountController} from '../../../api/credit/credit-account.controller';
import {StatusCallAccountController} from '../../../api/status-calls/status-call-account.controller';
import {Column, SimpleTable} from '../../../components/SimpleTable';
import {CreditRequestModuleEnum} from '../../../model/credit/credit-request-module.enum';
import {CreditRequestPriorityEnum} from '../../../model/credit/credit-request-priority.enum';
import {CreditRequestStatusEnum} from '../../../model/credit/credit-request-status.enum';
import {CreditRequestModel} from '../../../model/credit/credit-request.model';
import {FormatUtils} from '../../../utils/format.utils';
import {CreditRequestUtils} from '../utils/credit-request.utils';

type Props = {
  requests: CreditRequestModel[]
  translateLocal: (key: string) => string
  translate: (key: string) => string
  onRequestPressed: (request: CreditRequestModel) => void
  onAttachmentPressed: (request: CreditRequestModel) => void
  onReorder?: (orderedIds: string[]) => void
}

type OrgGroup = {
  orgId: string
  orgName: string
  requests: CreditRequestModel[]
}

type OrgSummary = {
  loading: boolean
  creditBalance: number | null
  nextCallAt: string | null
}

export const CreditRequestGroupedView = ({requests, translateLocal, translate, onRequestPressed, onAttachmentPressed, onReorder}: Props) => {
  const translateModule = (m: CreditRequestModuleEnum) => translate(`credit-support.module.${m}`)
  const translateStatus = (s: CreditRequestStatusEnum) => translate(`credit-support.status.${s}`)
  const translatePriority = (p: CreditRequestPriorityEnum) => translate(`credit-support.priority.${p}`)

  const formatHours = (h: number | null | undefined): string =>
    (h === null || h === undefined) ? '-' : `${h.toFixed(1)} h`

  const groups: OrgGroup[] = useMemo(() => {
    const map = new Map<string, OrgGroup>()
    for (const r of requests) {
      const existing = map.get(r.organizationId)
      if (existing) {
        existing.requests.push(r)
      } else {
        map.set(r.organizationId, {
          orgId: r.organizationId,
          orgName: r.organizationName ?? '-',
          requests: [r],
        })
      }
    }
    const list = Array.from(map.values())
    for (const g of list) {
      g.requests.sort((a: CreditRequestModel, b: CreditRequestModel) => (a.backlogRank ?? 0) - (b.backlogRank ?? 0))
    }
    return list.sort((a, b) => a.orgName.localeCompare(b.orgName))
  }, [requests])

  const [summaries, setSummaries] = useState<Record<string, OrgSummary>>({})

  useEffect(() => {
    let cancelled = false
    const orgIds = groups.map(g => g.orgId)
    setSummaries(prev => {
      const next: Record<string, OrgSummary> = {}
      for (const id of orgIds) {
        next[id] = prev[id] ?? {loading: true, creditBalance: null, nextCallAt: null}
      }
      return next
    })
    Promise.all(orgIds.map(async (id) => {
      const [credit, statusCall] = await Promise.all([
        CreditAccountController.getCreditAccountSummary(id).catch(() => null),
        StatusCallAccountController.getStatusCallAccount(id).catch(() => null),
      ])
      const upcoming = statusCall?.upcomingCalls
        ?.filter(c => c.status === 'SCHEDULED')
        .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
      return [id, {
        loading: false,
        creditBalance: credit?.balanceHours ?? null,
        nextCallAt: upcoming?.[0]?.scheduledAt ?? null,
      }] as [string, OrgSummary]
    })).then(entries => {
      if (cancelled) return
      setSummaries(prev => {
        const next = {...prev}
        for (const [id, s] of entries) next[id] = s
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [groups])

  const buildStatus = (status: CreditRequestStatusEnum) => {
    const Icon = CreditRequestUtils.getStatusIcon(status)
    return <Box display="flex" alignItems="center" gap={1}>
      <Icon style={{color: CreditRequestUtils.getStatusIconColor(status)}}/>
      {translateStatus(status)}
    </Box>
  }

  const columns: Column<CreditRequestModel>[] = [
    {key: 'id', label: translateLocal('column.id'), render: (row) =>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton onClick={() => onRequestPressed(row)} aria-label={translate('common.details.title')}>
            <InfoOutlinedIcon/>
          </IconButton>
          <IconButton onClick={() => onAttachmentPressed(row)} aria-label={translate('common.details.title')}>
            <Folder/>
          </IconButton>
          <Typography variant="body2">{row.id.substring(0, 8)}</Typography>
        </Box>
    },
    {key: 'title', label: translateLocal('column.title'), render: (row) => row.title},
    {key: 'module', label: translateLocal('column.module'), render: (row) => translateModule(row.module)},
    {key: 'priority', label: translateLocal('column.priority'), render: (row) => translatePriority(row.priority)},
    {key: 'account', label: translateLocal('column.account'), render: (row) => row.accountName ?? '-'},
    {key: 'estimated', label: translateLocal('column.hoursEstimated'), render: (row) => formatHours(row.hoursEstimated)},
    {key: 'actual', label: translateLocal('column.actualHours'), render: (row) => formatHours(row.actualHoursSpent)},
    {key: 'owner', label: translateLocal('column.owner'), render: (row) => row.ownerAccountName ?? '-'},
    {key: 'status', label: translateLocal('column.status'), render: (row) => buildStatus(row.status)},
    {key: 'createDate', label: translateLocal('column.createDate'), render: (row) => FormatUtils.formatDate(row.createDate)},
  ]

  return <Box display="flex" flexDirection="column" gap={1}>
    {groups.map(g => {
      const s = summaries[g.orgId]
      const balanceLabel = !s || s.loading
        ? '...'
        : s.creditBalance === null ? '-' : `${s.creditBalance.toFixed(1)} h`
      const nextCallLabel = !s || s.loading
        ? '...'
        : s.nextCallAt ? FormatUtils.formatDate(s.nextCallAt) : translateLocal('grouped.noUpcomingCall')
      const awaitingUs = g.requests.filter(r =>
        r.status === CreditRequestStatusEnum.NEEDS_ESTIMATION || r.status === CreditRequestStatusEnum.ESTIMATE_REJECTED
      ).length
      return <Accordion key={g.orgId} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
          <Box display="flex" alignItems="center" gap={2} flex={1} flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight={600}>{g.orgName}</Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon fontSize="small" color="action"/>
              <Typography variant="body2">{translateLocal('grouped.balance')}: {balanceLabel}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <EventIcon fontSize="small" color="action"/>
              <Typography variant="body2">{translateLocal('grouped.nextCall')}: {nextCallLabel}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <PriorityHighIcon fontSize="small" color={awaitingUs > 0 ? 'warning' : 'action'}/>
              <Typography
                variant="body2"
                color={awaitingUs > 0 ? 'warning.main' : 'text.primary'}
                fontWeight={awaitingUs > 0 ? 600 : 400}
              >
                {translateLocal('grouped.awaitingUs')}: {awaitingUs}
              </Typography>
            </Box>
            <Box ml="auto">
              <Typography variant="caption" color="text.secondary">
                {translateLocal('grouped.ticketsCount')}: {g.requests.length}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <SimpleTable
            rows={g.requests}
            columns={columns}
            rowKey={(row) => row.id}
            onReorder={onReorder ? (orderedKeys) => onReorder(orderedKeys.map(String)) : undefined}
          />
        </AccordionDetails>
      </Accordion>
    })}
  </Box>
}
